/**
 * DARS Admin Portal — router.
 *
 * Structure mirrors solutions/assist/frontend/agent-assist-portal/src/router/index.ts:
 *   /login, /forgot-password → auth
 *   /                        → MainLayout with nested routes
 *   /config                  → single ConfigurationView (sub-nav inside)
 *   /analytics/hearings      → HearingInsights (search-by-ID + KPI + timeline)
 *
 * All routes gate on requiredSolution='dars' via router.beforeEach.
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore, useEntitlements } from '@adaptcx/shared'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false, layout: 'auth' },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredSolution: 'dars' },
    children: [
      // ── Dashboard ────────────────────────────────────
      { path: '', name: 'Dashboard', component: () => import('@/views/DashboardView.vue') },

      // ── Profile settings (from user menu) ────────────
      { path: 'profile', name: 'profile-settings', component: () => import('@/views/profile/ProfileSettingsView.vue') },

      // ── Hearings ─────────────────────────────────────
      { path: 'hearings/live',          name: 'HearingsLive',          component: () => import('@/views/HearingsLiveView.vue'),
        meta: { title: 'Live Hearings', icon: 'stream' } },
      { path: 'hearings/history',       name: 'HearingsHistory',       component: () => import('@/views/HearingsHistoryView.vue'),
        meta: { title: 'Hearing History', icon: 'history' } },
      { path: 'hearings/audio-streams', name: 'HearingsAudioStreams',  component: () => import('@/views/StubView.vue'),
        meta: {
          title: 'Audio Streams',
          subtitle: 'Live audiohook streams from active hearings.',
          icon: 'hearing',
          emptyTitle: 'No active audio streams',
          emptyCopy: 'Live audiohook streams from in-session hearings show up here — one row per participant channel with transcription state.',
        } },
      { path: 'hearings/recordings',    name: 'HearingsRecordings',    component: () => import('@/views/RecordingsView.vue'),
        meta: { title: 'Recordings', icon: 'mic' } },

      // ── Cases ────────────────────────────────────────
      { path: 'cases',         name: 'Cases',        component: () => import('@/views/CasesListView.vue'),
        meta: { title: 'Cases', icon: 'folder' } },
      { path: 'cases/packets', name: 'CasePackets',  component: () => import('@/views/CasePacketsListView.vue'),
        meta: { title: 'Case Packets', icon: 'inventory_2' } },
      { path: 'cases/packets/:id', name: 'CasePacketDetail', component: () => import('@/views/PacketDetailsView.vue'), props: true,
        meta: { title: 'Packet Details', icon: 'inventory_2' } },
      { path: 'cases/search',  name: 'CaseSearch',   component: () => import('@/views/StubView.vue'),
        meta: {
          title: 'Search & Discovery',
          subtitle: 'Full-text and structured search across cases.',
          icon: 'search',
          emptyTitle: 'Search across cases',
          emptyCopy: 'Full-text and structured search across cases, participants, dispositions, and documents will land here.',
        } },

      // ── Analytics (Hearing Insights is the real audit view) ─
      { path: 'analytics/hearings',   name: 'HearingInsights',    component: () => import('@/views/HearingInsights.vue') },
      { path: 'analytics/cases',      name: 'CaseInsights',       component: () => import('@/views/CaseInsightsView.vue'),
        meta: { title: 'Case Insights', icon: 'insights' } },
      { path: 'analytics/officer',    name: 'OfficerPerformance', component: () => import('@/views/OfficerPerformanceView.vue'),
        meta: { title: 'Officer Performance', icon: 'speed' } },
      { path: 'analytics/compliance', name: 'ComplianceAnalytics',component: () => import('@/views/ComplianceAnalyticsView.vue'),
        meta: { title: 'Compliance', icon: 'shield' } },

      // ── Configuration (single view with sub-nav for all config sections) ─
      { path: 'config', name: 'Configuration', component: () => import('@/views/ConfigurationView.vue') },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    await authStore.fetchUser()
    if (!authStore.isAuthenticated) {
      return next({ name: 'Login', query: { redirect: to.fullPath } })
    }
  }
  next()
})

router.beforeEach(async (to, _from, next) => {
  const requiredSolution = to.meta.requiredSolution as string | undefined
  if (!requiredSolution) return next()
  const { isSolutionLicensed, loaded, load } = useEntitlements()
  if (!loaded.value) await load()
  if (isSolutionLicensed(requiredSolution)) return next()
  next({ name: 'Login', query: { solutionLocked: requiredSolution } })
})

export default router
